import { Eye, History, LeafIcon, Edit, Trash2, UploadCloudIcon, PlaneTakeoff, Rewind } from "lucide-react";
import { ContainerCard, ModuleTitleCard, EmployeeDetails } from "../../components/ui/cards";
import { useCallback, useEffect, useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { EMPLOYEE_LEAVE_POLICY, LEAVE_URL } from "../../constants/api";
import { truncateText } from "../../components/common/CommonFunctions";
import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import { Textarea } from "../../components/ui/input";
import { DatePicker, Select, Upload as AntUpload, Image, TimePicker } from "antd";
import dayjs from 'dayjs';
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";

const LeaveRequest = () => {
    // Constants
    const FILE_TYPES = {
        IMAGE: "image/",
        PDF: "application/pdf"
    };
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

    // states
    const companyid = sessionStorage.getItem("userId");
    const { fetch, create, update } = useApiMethods()
    const [formData, setFormData] = useState([])
    const [totalRecords, setTotalRecords] = useState(0)
    const [openDialog, setOpenDialog] = useState(false)
    const [loading, setLoading] = useState(false);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showApprovalLog, setShowApprovalLog] = useState(false);
    const [dropdownData, setDropdownData] = useState({
        leaveTypes: []
    });
    const [screenLoader, setScreenLoader] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteRecord, setDeleteRecord] = useState(null);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [cancelRecord, setCancelRecord] = useState(null);
    const [cancelReason, setCancelReason] = useState("");

    // Form states - UPDATED FORM STATE NAMES
    const [formState, setFormState] = useState({
        leave_request_type: "leave",
        leave_type: "",
        period: "",
        from_date: undefined,
        to_date: undefined,
        rejoining_date: undefined,
        total_days: "",
        premission_date: "",
        start_time: "",
        end_time: "",
        total_duration: "",
        reason: "",
        company_id: parseInt(companyid),
        is_active: true,
        uploaded_documents: [],
    });

    const getLeaveList = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_LEAVE_POLICY, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const fetchDropdown = async () => {
        setScreenLoader(true)
        setLoading(true)

        const [leaveTypes] = await Promise.all([
            getLeaveList()
        ]);

        setDropdownData({
            leaveTypes: leaveTypes || []
        });
        setLoading(false);
        setScreenLoader(false);
    }

    useEffect(() => {
        const initialize = async () => {
            await fetchDropdown();
        }

        initialize();
    }, []);

    const StatusBadge = ({ status }) => {
        const getStatusConfig = (status) => {
            const config = {
                pending: {
                    text: 'Pending',
                    className: 'bg-yellow-100 text-yellow-700 border-yellow-200'
                },
                approved: {
                    text: 'Approved',
                    className: 'bg-green-100 text-green-700 border-green-200'
                },
                rejected: {
                    text: 'Rejected',
                    className: 'bg-red-100 text-red-700 border-red-200'
                },
                cancelled: {
                    text: 'Cancelled',
                    className: 'bg-gray-100 text-gray-600 border-gray-200'
                }
            };

            return config[status] || {
                text: status?.charAt(0).toUpperCase() + status?.slice(1) || 'Pending',
                className: 'bg-blue-100 text-blue-700 border-blue-200'
            };
        };

        const statusConfig = getStatusConfig(status);

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${statusConfig.className}`}>
                {statusConfig.text}
            </span>
        );
    };

    // Time Chip Component
    const TimeChip = ({ time, type = "start" }) => {
        if (!time || time === '-') return null;

        const chipClass = type === "start"
            ? "bg-blue-100 text-blue-700 border-blue-200"
            : "bg-orange-100 text-orange-700 border-orange-200";

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${chipClass}`}>
                {formatTimeToAMPM(time)}
            </span>
        );
    };

    // Format time to AM/PM
    const formatTimeToAMPM = (timeString) => {
        if (!timeString || timeString === '-') return '';

        try {
            // Handle different time formats
            let time = timeString;
            if (timeString.includes(' ')) {
                // If it's a full datetime string, extract time part
                time = timeString.split(' ')[1];
            }

            const [hours, minutes] = time.split(':');
            const hour = parseInt(hours);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const formattedHour = hour % 12 || 12;

            return `${formattedHour}:${minutes} ${ampm}`;
        } catch {
            return timeString;
        }
    };

    // Format request type for better display
    const formatRequestType = (type) => {
        const typeMap = {
            'work_from_home': 'Work From Home',
            'permission': 'Permission',
            'leave': 'Leave'
        };
        return typeMap[type] || type;
    };

    // Format date for display
    const formatDateDisplay = (dateString) => {
        if (!dateString) return '-';

        console.log('Original dateString:', dateString); // "24-11-2025 05:00:00 PM"

        try {
            // Handle date strings in "DD-MM-YYYY" format
            if (dateString.includes('-') && dateString.split('-')[0].length === 2) {
                const [day, month, year] = dateString.split('-');

                // Handle cases where there might be time part after the date
                const cleanYear = year.split(' ')[0]; // Remove any time part from year
                const date = new Date(`${cleanYear}-${month}-${day}`);

                // Validate the date
                if (isNaN(date.getTime())) {
                    return dateString;
                }

                const formatted = date.toLocaleDateString('en-GB');
                console.log('Formatted DD-MM-YYYY:', formatted);
                return formatted;
            }

            // Handle date-time strings like "24-11-2025 05:00:00 PM"
            if (dateString.includes(' ') && dateString.includes('-')) {
                const [datePart, timePart, period] = dateString.split(' ');
                const [day, month, year] = datePart.split('-');

                // Convert 12-hour format to 24-hour format if needed
                let cleanTimePart = timePart;
                if (period && (period === 'PM' || period === 'AM')) {
                    const [hours, minutes, seconds] = timePart.split(':');
                    let hourInt = parseInt(hours, 10);

                    if (period === 'PM' && hourInt < 12) {
                        hourInt += 12;
                    } else if (period === 'AM' && hourInt === 12) {
                        hourInt = 0;
                    }

                    cleanTimePart = `${hourInt.toString().padStart(2, '0')}:${minutes}:${seconds}`;
                }

                const date = new Date(`${year}-${month}-${day}T${cleanTimePart}`);

                if (isNaN(date.getTime())) {
                    return dateString;
                }

                const formatted = date.toLocaleDateString('en-GB');
                console.log('Formatted date-time:', formatted);
                return formatted;
            }

            // For other date formats, try direct parsing
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString; // Return original if invalid
            }

            const formatted = date.toLocaleDateString('en-GB');
            console.log('Formatted other format:', formatted);
            return formatted;

        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString;
        }
    };

    // Check if request date has passed
    const isRequestDatePassed = (fromDate) => {
        if (!fromDate) return false;

        console.log('Checking date:', fromDate); // "24-11-2025 06:00:00 PM"

        try {
            let requestDate;

            // Extract only the date part if there's time information
            let datePart = fromDate;
            if (fromDate.includes(' ')) {
                datePart = fromDate.split(' ')[0];
            }

            // Handle date strings in "DD-MM-YYYY" or "DD/MM/YYYY" format
            if ((datePart.includes('-') || datePart.includes('/')) && datePart.split(/[-/]/)[0].length === 2) {
                const separator = datePart.includes('-') ? '-' : '/';
                const [day, month, year] = datePart.split(separator);

                // Validate date components
                if (!day || !month || !year) {
                    return false;
                }

                // Create date object with proper format (YYYY-MM-DD)
                requestDate = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            } else {
                // For other formats, try direct parsing
                requestDate = new Date(datePart);
            }

            // Check if the date is valid
            if (isNaN(requestDate.getTime())) {
                console.warn('Invalid date format:', fromDate);
                return false;
            }

            const today = new Date();

            // Reset both dates to start of day for accurate comparison
            requestDate.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);

            const isPassed = requestDate < today;
            console.log(`Date comparison: ${requestDate.toISOString()} < ${today.toISOString()} = ${isPassed}`);

            return isPassed;

        } catch (error) {
            console.error('Error checking date:', error);
            return false;
        }
    };

    // File validation and handling
    const validateFile = (file) => {
        const isImage = file.type?.startsWith(FILE_TYPES.IMAGE);
        const isPDF = file.type === FILE_TYPES.PDF;

        if (!isImage && !isPDF) {
            ToastMsg("error", "Only image and PDF files are allowed!");
            return false;
        }

        if (file.size > MAX_FILE_SIZE) {
            ToastMsg("error", "File size must be less than 5MB!");
            return false;
        }

        return true;
    };

    const handleFileChange = useCallback((file) => {
        if (!file || file.length === 0) {
            setFormState(prev => ({ ...prev, uploaded_documents: [] }));
            return;
        }

        const uploadedFile = Array.isArray(file) ? file[0] : file;

        if (validateFile(uploadedFile)) {
            setFormState(prev => ({
                ...prev,
                uploaded_documents: [uploadedFile]
            }));
        }
    }, []);

    // Handle form field changes
    const handleFormChange = (field, value) => {
        setFormState(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // columns
    const columns = [
        {
            Header: 'Request Type',
            accessor: 'leave_request_type',
            filterable: true,
            sortable: true,
            width: '160px',
            Cell: ({ value }) => {
                if (!value) {
                    return (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600">
                            Not Mentioned
                        </span>
                    );
                }

                const getRequestTypeConfig = (type) => {
                    const config = {
                        work_from_home: {
                            text: 'Work From Home',
                            className: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-300 dark:border-purple-700',
                            icon: '🏠'
                        },
                        permission: {
                            text: 'Permission',
                            className: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-700',
                            icon: '⏱️'
                        },
                        leave: {
                            text: 'Leave',
                            className: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700',
                            icon: '🌴'
                        }
                    };

                    const defaultConfig = {
                        text: type.split('_').map(word =>
                            word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' '),
                        className: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600',
                        icon: '📝'
                    };

                    return config[type] || defaultConfig;
                };

                const typeConfig = getRequestTypeConfig(value);

                return (
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${typeConfig.className}`}>
                        <span className="text-xs">{typeConfig.icon}</span>
                        {typeConfig.text}
                    </span>
                );
            }
        },
        {
            Header: 'Leave Type / Period',
            accessor: 'leave_type',
            sortable: true,
            filterable: true,
            width: '200px',
            Cell: ({ value, row }) => {
                const leaveNameFromId = dropdownData.leaveTypes.find(l => l.id === value || l.id === parseInt(value))?.leave_name;
                const displayLeave = leaveNameFromId || (value && value !== '-' ? value : formatRequestType(row.leave_request_type));
                return (
                    <div className="flex items-center gap-2">
                        <span className="text-gray-700 dark:text-gray-300">
                            {displayLeave}
                        </span>
                        {row.period && row.period !== '-' && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border border-purple-300 dark:border-purple-700 w-fit">
                                <span className="text-xs">📅</span>
                                {row.period.replace('_', ' ')}
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            Header: 'From Date',
            accessor: 'from_date',
            sortable: true,
            filterable: true,
            filterType: 'date',
            Cell: ({ value, row }) => {
                return (
                    <div className="flex items-center gap-1">
                        <span className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {formatDateDisplay(value)}
                        </span>
                        {row.leave_request_type === 'permission' && (
                            <TimeChip time={row.start_time} type="start" />
                        )}
                    </div>
                );
            },
            width: '180px'
        },
        {
            Header: 'To Date',
            accessor: 'to_date',
            sortable: true,
            filterable: true,
            filterType: 'date',
            Cell: ({ value, row }) => (
                <div className="flex items-center gap-1">
                    <span className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {formatDateDisplay(value)}
                    </span>
                    {row.leave_request_type === 'permission' && (
                        <TimeChip time={row.end_time} type="end" />
                    )}
                </div>
            ),
            width: '180px'
        },
        {
            Header: 'Status',
            accessor: 'approval_status',
            sortable: true,
            filterable: true,
            filterType: 'requestStatus',
            Cell: ({ value }) => <StatusBadge status={value} />,
            width: '150px'
        },
        {
            Header: 'Reason',
            accessor: 'reason',
            Cell: ({ value }) => truncateText(value, 22, "300px", "balance"),
            width: '150px',
        },
        {
            Header: 'Actions',
            accessor: 'actions',
            sortable: false,
            fixed: true,
            align: 'right',
            width: '80px',
            Cell: ({ row }) => {
                const isPending = row.approval_status === 'pending';
                const datePassed = isRequestDatePassed(row.from_date);
                const isRequestCancelled = row.approval_status === 'request cancelled';

                console.log(row);

                console.log(datePassed);

                console.log(isRequestCancelled);


                return (
                    <div className="flex justify-end gap-2">
                        {isPending ? (
                            <>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        setIsEdit(true);
                                        setEditRecord(row);
                                        const matchedLeave = dropdownData.leaveTypes.find(leave =>
                                            leave.id === row.leave_type ||
                                            leave.id === parseInt(row.leave_type) ||
                                            leave.leave_name === row.leave_type ||
                                            leave.leave_name?.toLowerCase() === String(row.leave_type)?.toLowerCase()
                                        );
                                        const leaveTypeId = matchedLeave ? matchedLeave.id : (parseInt(row.leave_type) || row.leave_type || '');
                                        setFormState({
                                            leave_request_type: row.leave_request_type || 'leave',
                                            leave_type: leaveTypeId,
                                            period: row.period || '',
                                            from_date: row.from_date ? dayjs(row.from_date, 'DD-MM-YYYY') : null,
                                            to_date: row.to_date ? dayjs(row.to_date, 'DD-MM-YYYY') : null,
                                            start_time: row.start_time ? dayjs(row.start_time, 'HH:mm') : null,
                                            end_time: row.end_time ? dayjs(row.end_time, 'HH:mm') : null,
                                            reason: row.reason || '',
                                            company_id: parseInt(companyid),
                                            is_active: true,
                                            uploaded_documents: [],
                                            rejoining_date: undefined,
                                            total_days: "",
                                            premission_date: "",
                                            total_duration: "",
                                        });
                                        setOpenDialog(true);
                                    }}
                                    disabled={datePassed}
                                    className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${datePassed
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                        : 'bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 hover:text-blue-700'
                                        }`}
                                    title={datePassed ? "Cannot edit - Request date has passed" : "Edit Request"}
                                >
                                    <Edit className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        setDeleteRecord(row);
                                        setShowDeleteConfirm(true);
                                    }}
                                    disabled={datePassed}
                                    className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${datePassed
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                        : 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:text-red-700'
                                        }`}
                                    title={datePassed ? "Cannot delete - Request date has passed" : "Delete Request"}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        setSelectedRequest(row);
                                        setShowApprovalLog(true);
                                    }}
                                    disabled={!row.approval_log || row.approval_log.length === 0}
                                    className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${!row.approval_log || row.approval_log.length === 0
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                        : 'bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 hover:text-amber-700'
                                        }`}
                                    title={(!row.approval_log || row.approval_log.length === 0) ? "No approval log available" : "View Approval Log"}
                                >
                                    <History className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => {
                                        setCancelRecord(row);
                                        setCancelReason("");
                                        setShowCancelDialog(true);
                                    }}
                                    disabled={row.approval_status === 'cancelled' || datePassed || isRequestCancelled}
                                    className={`h-7 w-7 p-0 rounded-md transition-all duration-200 shadow-sm hover:scale-105 ${row.approval_status === 'cancelled' || datePassed || isRequestCancelled
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                        : 'bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100 hover:text-orange-700'
                                        }`}
                                    title={row.approval_status === 'cancelled'
                                        ? "Cannot cancel - Request already cancelled"
                                        : datePassed || isRequestCancelled
                                            ? "Cannot cancel - Request date has passed"
                                            : "Cancel Request"}
                                >
                                    <Rewind className="h-3.5 w-3.5" />
                                </Button>
                            </>
                        )}
                    </div>
                )
            }
        }
    ];

    // Handle form submission - UPDATED TO USE NEW FORM STATE NAMES
    const handleSubmitRequest = async () => {
        // Validation based on request type
        if (formState.leave_request_type === 'permission') {
            if (!formState.from_date || !formState.start_time || !formState.end_time || !formState.reason) {
                ToastMsg('error', 'Please fill in all required fields for permission');
                return;
            }
        } else if (formState.leave_request_type === 'work_from_home') {
            if (!formState.period || !formState.from_date || !formState.to_date || !formState.reason) {
                ToastMsg('error', 'Please fill in all required fields for work from home');
                return;
            }
        } else if (formState.leave_request_type === 'leave') {
            if (!formState.leave_type || !formState.period || !formState.from_date || !formState.to_date || !formState.reason) {
                ToastMsg('error', 'Please fill in all required fields for leave');
                return;
            }
        }

        setLoading(true);

        try {
            const dataForm = new FormData();

            // Append basic fields
            dataForm.append("leave_request_type", formState.leave_request_type || "");
            dataForm.append("reason", formState.reason || "");
            dataForm.append("is_active", formState.is_active.toString());
            dataForm.append("company", formState.company_id.toString());
            dataForm.append("request_type", "leave request");
            dataForm.append("approval_status", "pending");

            // Append conditional fields
            if (formState.leave_type) {
                dataForm.append("leave_type", formState.leave_type);
            }

            if (formState.period) {
                dataForm.append("period", formState.period);
            }

            if (formState.from_date) {
                dataForm.append("from_date", dayjs(formState.from_date).format("YYYY-MM-DD"));
            }

            if (formState.to_date) {
                dataForm.append("to_date", dayjs(formState.to_date).format("YYYY-MM-DD"));
            }

            if (formState.start_time) {
                dataForm.append("start_time", dayjs(formState.start_time).format("HH:mm:ss"));
            }

            if (formState.end_time) {
                dataForm.append("end_time", dayjs(formState.end_time).format("HH:mm:ss"));
            }

            if (formState.premission_date) {
                dataForm.append("premission_date", dayjs(formState.premission_date).format("YYYY-MM-DD"));
            }

            // Handle total_duration for permission
            if (formState.leave_request_type === 'permission' && formState.start_time && formState.end_time) {
                const start = dayjs(formState.start_time);
                const end = dayjs(formState.end_time);
                const duration = end.diff(start, 'second');
                const hours = Math.floor(duration / 3600);
                const minutes = Math.floor((duration % 3600) / 60);
                const seconds = duration % 60;
                const formattedDuration = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
                dataForm.append("total_duration", formattedDuration);
            }

            // Handle file uploads
            if (formState.uploaded_documents && formState.uploaded_documents.length > 0) {
                formState.uploaded_documents.forEach((file, index) => {
                    if (file instanceof File) {
                        dataForm.append(`uploaded_documents_${index + 1}`, file);
                    }
                });
            } else {
                dataForm.append("uploaded_documents", []);
            }

            // Add id for edit
            if (isEdit && editRecord.id) {
                dataForm.append("id", editRecord.id);
            }

            const response = await (isEdit ? update(LEAVE_URL, dataForm, true) : create(LEAVE_URL, dataForm, true));

            if (response.status !== 'fail') {
                ToastMsg('success', isEdit ? 'Leave request updated successfully' : response.message);
                setOpenDialog(false);
                setIsEdit(false);
                setEditRecord(null);
                // Reset form state
                setFormState({
                    leave_request_type: "leave",
                    leave_type: "",
                    period: "",
                    from_date: undefined,
                    to_date: undefined,
                    rejoining_date: undefined,
                    total_days: "",
                    premission_date: "",
                    start_time: "",
                    end_time: "",
                    total_duration: "",
                    reason: "",
                    company_id: parseInt(companyid),
                    is_active: true,
                    uploaded_documents: [],
                });
                setServerTrigger(!serverTrigger); // Refresh the table
            } else {
                ToastMsg('error', response.message || 'Failed to submit request');
            }
        } catch (error) {
            ToastMsg('error', 'An error occurred while submitting the request');
            console.error('Submission error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle delete with confirmation
    const handleDeleteRequest = async () => {
        if (!deleteRecord) return;

        setLoading(true);
        try {
            const response = await update(LEAVE_URL, {
                id: deleteRecord.id,
                is_active: false
            });

            if (response.status) {
                ToastMsg('success', 'Leave request deleted successfully');
                setShowDeleteConfirm(false);
                setDeleteRecord(null);
                setServerTrigger(!serverTrigger); // Refresh the table
            } else {
                ToastMsg('error', response.message || 'Failed to delete request');
            }
        } catch (error) {
            ToastMsg('error', 'An error occurred while deleting the request');
            console.error('Delete error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle cancel request
    const handleCancelRequest = async () => {
        if (!cancelRecord || !cancelReason.trim()) {
            ToastMsg('error', 'Please provide a cancel reason');
            return;
        }

        setLoading(true);
        try {
            const response = await update(LEAVE_URL, {
                ...cancelRecord,
                cancel_reason: cancelReason,
                request_type: "cancel request",
                approval_status: 'cancel inprogress'
            });

            if (response.status) {
                ToastMsg('success', 'Request cancelled successfully');
                setShowCancelDialog(false);
                setCancelRecord(null);
                setCancelReason("");
                setServerTrigger(!serverTrigger);
            } else {
                ToastMsg('error', response.message || 'Failed to cancel request');
            }
        } catch (error) {
            ToastMsg('error', 'An error occurred while cancelling the request');
            console.error('Cancel error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Reset form function
    const resetForm = () => {
        setIsEdit(false);
        setEditRecord(null);
        setFormState({
            leave_request_type: "leave",
            leave_type: "",
            period: "",
            from_date: undefined,
            to_date: undefined,
            rejoining_date: undefined,
            total_days: "",
            premission_date: "",
            start_time: "",
            end_time: "",
            total_duration: "",
            reason: "",
            company_id: parseInt(companyid),
            is_active: true,
            uploaded_documents: [],
        });
    };

    return (
        <ContainerCard>
            {/* title */}
            <ModuleTitleCard
                Icon={PlaneTakeoff}
                title={'Leave Request'}
                subtitle={'Rise a request for you pre planned or sudden leave or permission'}
                totalRecords={totalRecords}
                buttonEnable
                setOpenDialog={setOpenDialog}
                buttonName={'Add Leave Request'}
            />

            {/* Data Table */}
            <Reactable
                columns={columns}
                data={formData}
                setData={setFormData}
                setTotalCount={setTotalRecords}
                serverSide
                url={LEAVE_URL}
                serverTrigger={serverTrigger}
                tableHeight="calc(100vh - 320px)"
                rowSize={"md"}
                isCSVRequired={true}
                isPDFRequired={true}
                isDraftRequired={true}
            />

            {/* Approval Log Dialog */}
            <DialogModal
                open={showApprovalLog}
                onClose={() => setShowApprovalLog(false)}
                title="Approval Log"
                showActions={false}
                width="750px"
            >
                {selectedRequest && selectedRequest.approval_log && selectedRequest.approval_log.length > 0 ? (
                    <div className="space-y-4">
                        <div className="overflow-x-auto h-60 relative">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50">
                                            Date & Time
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50">
                                            Approver
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50">
                                            Notes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {selectedRequest.approval_log.map((log, index) => (
                                        <tr key={index} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                {new Date(log.approved_date_and_time).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                {log.approver_name || '-'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <StatusBadge status={log.approval_status} />
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                {log.approval_notes || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="text-sm flex items-center justify-between gap-2 text-gray-600 border-t pt-4">
                            <div>
                                {(() => {
                                    const record = selectedRequest;
                                    let empDetails = {
                                        ...record?.employee,
                                        designation_id: record?.employee?.designation
                                            ? {
                                                id: record?.employee.designation.id,
                                                designation_name: record?.employee.designation.name,
                                            }
                                            : { id: 0, designation_name: "" },
                                        first_name: record?.employee?.employee_name || "-",
                                        id: record?.employee?.employee_id || "-",
                                    };
                                    return <EmployeeDetails details={empDetails} size="xs" />;
                                })()}
                            </div>
                            <p><strong>Request Type:</strong> {formatRequestType(selectedRequest.leave_request_type)}</p>
                            <p><strong>Current Status:</strong> <StatusBadge status={selectedRequest.approval_status} /></p>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-gray-500">No approval log available for this request.</p>
                    </div>
                )}
            </DialogModal>

            {/* Add Leave Request Dialog - UPDATED TO USE NEW FORM STATE NAMES */}
            <DialogModal
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    resetForm();
                }}
                onSave={handleSubmitRequest}
                title={isEdit ? "Edit Leave Request" : "Apply Leave Request"}
                saveLabel={isEdit ? "Update Request" : "Submit Request"}
                width="700px"
                loading={loading}
            >
                <div className={cn("grid gap-8 grid-cols-1 md:grid-cols-2", "max-h-90 overflow-y-auto pr-2")}>

                    {/* Left Column */}
                    <div className="space-y-4">
                        {/* Request Type */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Request Type <span className="text-red-500">*</span>
                            </label>
                            <Select
                                value={formState.leave_request_type}
                                onChange={(value) => handleFormChange('leave_request_type', value)}
                                placeholder="Select request type"
                                className="w-full"
                                size="large"
                            >
                                <Select.Option value="leave">Leave</Select.Option>
                                <Select.Option value="permission">Permission</Select.Option>
                                <Select.Option value="work_from_home">Work From Home</Select.Option>
                            </Select>
                        </div>

                        {formState.leave_request_type === 'permission' && (
                            <>
                                {/* Date */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Date <span className="text-red-500">*</span>
                                    </label>
                                    <DatePicker
                                        value={formState.from_date}
                                        onChange={(date) => handleFormChange('from_date', date)}
                                        placeholder="Select date"
                                        className="w-full"
                                        size="large"
                                        format="DD-MM-YYYY"
                                    />
                                </div>

                                {/* Start Time */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Start Time <span className="text-red-500">*</span>
                                    </label>
                                    <TimePicker
                                        value={formState.start_time}
                                        onChange={(time) => handleFormChange('start_time', time)}
                                        placeholder="Select start time"
                                        className="w-full"
                                        size="large"
                                        format="hh:mm A"
                                    />
                                </div>

                                {/* End Time */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        End Time <span className="text-red-500">*</span>
                                    </label>
                                    <TimePicker
                                        value={formState.end_time}
                                        onChange={(time) => handleFormChange('end_time', time)}
                                        placeholder="Select end time"
                                        className="w-full"
                                        size="large"
                                        format="hh:mm A"
                                    />
                                </div>
                            </>
                        )}

                        {formState.leave_request_type === 'work_from_home' && (
                            <>
                                {/* Period */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Select Period <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        value={formState.period}
                                        onChange={(value) => handleFormChange('period', value)}
                                        placeholder="Select period"
                                        className="w-full"
                                        size="large"
                                    >
                                        <Select.Option value="full_day">Full Day</Select.Option>
                                        <Select.Option value="first_half">First Half</Select.Option>
                                        <Select.Option value="second_half">Second Half</Select.Option>
                                    </Select>
                                </div>

                                {/* From Date and To Date */}
                                <div className="grid gap-4 grid-cols-2">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            From Date <span className="text-red-500">*</span>
                                        </label>
                                        <DatePicker
                                            value={formState.from_date}
                                            onChange={(date) => handleFormChange('from_date', date)}
                                            placeholder="Select from date"
                                            className="w-full"
                                            size="large"
                                            format="DD-MM-YYYY"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            To Date <span className="text-red-500">*</span>
                                        </label>
                                        <DatePicker
                                            value={formState.to_date}
                                            onChange={(date) => handleFormChange('to_date', date)}
                                            placeholder="Select to date"
                                            className="w-full"
                                            size="large"
                                            format="DD-MM-YYYY"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {formState.leave_request_type === 'leave' && (
                            <>
                                {/* Leave Type */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Leave Type <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        value={formState.leave_type ? (parseInt(formState.leave_type) || formState.leave_type) : undefined}
                                        onChange={(value) => handleFormChange('leave_type', value)}
                                        placeholder="Select leave type"
                                        className="w-full"
                                        size="large"
                                    >
                                        {dropdownData.leaveTypes.map((leave) => (
                                            <Select.Option key={leave.id} value={leave.id}>
                                                {leave.leave_name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>

                                {/* Period */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Select Period <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        value={formState.period}
                                        onChange={(value) => handleFormChange('period', value)}
                                        placeholder="Select period"
                                        className="w-full"
                                        size="large"
                                    >
                                        <Select.Option value="full_day">Full Day</Select.Option>
                                        <Select.Option value="first_half">First Half</Select.Option>
                                        <Select.Option value="second_half">Second Half</Select.Option>
                                    </Select>
                                </div>

                                {/* From Date and To Date */}
                                <div className="grid gap-4 grid-cols-2">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            From Date <span className="text-red-500">*</span>
                                        </label>
                                        <DatePicker
                                            value={formState.from_date}
                                            onChange={(date) => handleFormChange('from_date', date)}
                                            placeholder="Select from date"
                                            className="w-full"
                                            size="large"
                                            format="DD-MM-YYYY"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            To Date <span className="text-red-500">*</span>
                                        </label>
                                        <DatePicker
                                            value={formState.to_date}
                                            onChange={(date) => handleFormChange('to_date', date)}
                                            placeholder="Select to date"
                                            className="w-full"
                                            size="large"
                                            format="DD-MM-YYYY"
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                        {/* Reason */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Reason <span className="text-red-500">*</span>
                            </label>
                            <Textarea
                                value={formState.reason}
                                onChange={(e) => handleFormChange('reason', e.target.value)}
                                placeholder="Please provide a reason for your leave request..."
                                rows={5}
                                showCharCount={true}
                                maxLength={500}
                                className="w-full"
                            />
                        </div>

                        {/* File Upload */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Attachment
                            </label>
                            <div>
                                {(!formState.uploaded_documents || formState.uploaded_documents.length === 0) && (
                                    <AntUpload.Dragger
                                        accept="image/*,.pdf"
                                        maxCount={1}
                                        beforeUpload={() => false}
                                        onChange={({ file }) => handleFileChange(file)}
                                        showUploadList={false}
                                        className="dark:bg-gray-800 dark:border-gray-700"
                                    >
                                        <UploadCloudIcon className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                                            Click to upload document
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-500">
                                            Supported formats: .jpg, .png, .pdf (Max: 5MB)
                                        </p>
                                    </AntUpload.Dragger>
                                )}

                                {/* File Preview */}
                                {formState.uploaded_documents && formState.uploaded_documents.length > 0 && (
                                    <div className="mt-2">
                                        {formState.uploaded_documents[0].type?.startsWith("image/") ? (
                                            // IMAGE PREVIEW
                                            <div className="flex justify-between items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                                                <Image
                                                    src={URL.createObjectURL(formState.uploaded_documents[0])}
                                                    alt="preview"
                                                    width={120}
                                                    className="rounded-md shadow-sm"
                                                />
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleFormChange('uploaded_documents', [])}
                                                >
                                                    Remove
                                                </Button>
                                            </div>
                                        ) : formState.uploaded_documents[0].type === "application/pdf" ? (
                                            // PDF PREVIEW
                                            <div className="flex justify-between items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                                                <a
                                                    href={URL.createObjectURL(formState.uploaded_documents[0])}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sidebar underline underline-offset-2 text-sm flex items-center gap-2"
                                                >
                                                    Preview PDF <Eye className="w-3 h-3" />
                                                </a>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleFormChange('uploaded_documents', [])}
                                                >
                                                    Remove
                                                </Button>
                                            </div>
                                        ) : (
                                            // OTHER FILE PREVIEW
                                            <div className="flex justify-between items-center gap-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-800">
                                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                                    {formState.uploaded_documents[0].name}
                                                </span>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleFormChange('uploaded_documents', [])}
                                                >
                                                    Remove
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </DialogModal>

            {/* Delete Confirmation Dialog */}
            <DialogModal
                open={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false);
                    setDeleteRecord(null);
                }}
                onSave={handleDeleteRequest}
                title="Delete Leave Request"
                saveLabel="Delete"
                width="400px"
                loading={loading}
                saveButtonColor="destructive"
            >
                <div className="text-center py-4">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                        <Trash2 className="h-6 w-6 text-red-600" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                        Delete Leave Request
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete this leave request? This action cannot be undone.
                    </p>
                    {deleteRecord && (
                        <div className="mt-4 p-3 flex flex-col gap-2 bg-gray-50 dark:bg-gray-800 rounded-md">
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                <strong>Type:</strong> {formatRequestType(deleteRecord.leave_request_type)}
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                <strong>From Date:</strong> {formatDateDisplay(deleteRecord.from_date)}
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                <strong>Status:</strong> <StatusBadge status={deleteRecord.approval_status} />
                            </p>
                        </div>
                    )}
                </div>
            </DialogModal>

            {/* Cancel Request Dialog */}
            <DialogModal
                open={showCancelDialog}
                onClose={() => {
                    setShowCancelDialog(false);
                    setCancelRecord(null);
                    setCancelReason("");
                }}
                onSave={handleCancelRequest}
                title="Cancel Leave Request"
                saveLabel="Cancel Request"
                width="500px"
                loading={loading}
                saveButtonColor="destructive"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Please provide a reason for cancelling this leave request.
                    </p>
                    <Textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Enter cancel reason..."
                        rows={4}
                        showCharCount={true}
                        maxLength={500}
                        className="w-full"
                    />
                    {cancelRecord && (
                        <div className="p-3 flex justify-between items-center bg-gray-50 dark:bg-gray-800 rounded-md">
                            <p className="text-sm flex flex-col gap-2 text-gray-700 dark:text-gray-300">
                                <strong>Type:</strong> {(() => {
                                    const getRequestTypeConfig = (type) => {
                                        const config = {
                                            work_from_home: {
                                                text: 'Work From Home',
                                                className: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-300 dark:border-purple-700',
                                                icon: '🏠'
                                            },
                                            permission: {
                                                text: 'Permission',
                                                className: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-700',
                                                icon: '⏱️'
                                            },
                                            leave: {
                                                text: 'Leave',
                                                className: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700',
                                                icon: '🌴'
                                            }
                                        };

                                        const defaultConfig = {
                                            text: type.split('_').map(word =>
                                                word.charAt(0).toUpperCase() + word.slice(1)
                                            ).join(' '),
                                            className: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600',
                                            icon: '📝'
                                        };

                                        return config[type] || defaultConfig;
                                    };

                                    const typeConfig = getRequestTypeConfig(cancelRecord.leave_request_type);

                                    return (
                                        <span className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium border ${typeConfig.className}`}>
                                            <span className="text-xs">{typeConfig.icon}</span>
                                            {typeConfig.text}
                                        </span>
                                    );
                                })()}
                            </p>
                            <p className="text-sm flex flex-col gap-2 text-gray-700 dark:text-gray-300">
                                <strong>From Date:</strong> {formatDateDisplay(cancelRecord.from_date)}
                            </p>
                            <p className="text-sm flex flex-col gap-2 text-gray-700 dark:text-gray-300">
                                <strong>Status:</strong> <StatusBadge status={cancelRecord.approval_status} />
                            </p>
                        </div>
                    )}
                </div>
            </DialogModal>

        </ContainerCard>
    );
}

export default LeaveRequest;